/**
 * Parses pasted lists such as:
 * `John Doe <jdoe@company.com>; first last <name@company.com>`
 * Also accepts one address per line, or plain `user@host.com`.
 *
 * Separators: new lines, semicolons, and commas. Commas use heuristics so
 * `Last, First <email>` (no semicolon) stays a single entry; commas after
 * `>`, before `<`, or before a plain `user@host` address split.
 */
export type ParsedEmailInvite = {
  email: string;
  firstName: string;
  lastName: string;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ANGLE_EMAIL_RE = /<([^>\s]+@[^>\s]+)>/i;

/**
 * Splits pasted text on newlines, semicolons, and (when unambiguous) commas.
 */
function splitRawInviteSegments(raw: string): string[] {
  const out: string[] = [];
  let buf = '';
  let depth = 0;

  const flush = () => {
    const t = buf.trim();
    if (t) out.push(t);
    buf = '';
  };

  for (let i = 0; i < raw.length; i++) {
    const c = raw[i];
    if (c === '<') {
      depth++;
    } else if (c === '>') {
      depth = Math.max(0, depth - 1);
    }

    if (depth === 0 && (c === '\n' || c === ';')) {
      flush();
      continue;
    }

    if (depth === 0 && c === ',') {
      const before = buf.trimEnd();
      const after = raw.slice(i + 1);
      const afterTrim = after.trimStart();
      const firstToken = afterTrim.match(/^[^\s<]+/)?.[0] ?? '';
      const splitHere =
        before.endsWith('>') ||
        afterTrim.startsWith('<') ||
        (firstToken.length > 0 && EMAIL_RE.test(firstToken));

      if (splitHere) {
        flush();
        continue;
      }
    }

    buf += c;
  }
  flush();
  return out;
}

export function parseOutlookStyleEmailInvites(raw: string): ParsedEmailInvite[] {
  const results: ParsedEmailInvite[] = [];
  const seen = new Set<string>();

  const segments = splitRawInviteSegments(raw);

  for (const segment of segments) {
    let email = '';
    let displayName = '';

    const bracket = segment.match(ANGLE_EMAIL_RE);
    if (bracket) {
      email = bracket[1].trim().toLowerCase();
      displayName = segment
        .replace(ANGLE_EMAIL_RE, '')
        .trim()
        .replace(/^["']|["']$/g, '')
        .trim();
    } else if (EMAIL_RE.test(segment)) {
      email = segment.toLowerCase();
    } else {
      continue;
    }

    if (!EMAIL_RE.test(email)) continue;
    if (seen.has(email)) continue;
    seen.add(email);

    let firstName = '';
    let lastName = '';
    if (displayName) {
      const parts = displayName.split(/\s+/).filter(Boolean);
      if (parts.length === 1) {
        firstName = parts[0];
      } else if (parts.length > 1) {
        firstName = parts[0];
        lastName = parts.slice(1).join(' ');
      }
    }

    results.push({ email, firstName, lastName });
  }

  return results;
}
